import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit, OnDestroy, SimpleChanges, signal, WritableSignal, HostListener, computed, Signal, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AppText, searchQueryRequest, actionViewItem, ActionViewItemType, wholeLayerFeatureData, mapLayer, mapChange, Languages, LayerType } from '../interfaces/common';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { SearchQueryService } from '../services/search-query.service';
import { DisplayService } from '../services/display.service';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureService } from '../services/feature.service';
import { merge, Observable } from 'rxjs';
import { EditFeatureService } from '../map/query-views/services/edit-feature.service';
import { TableFilterListComponent } from './table-filter-list/table-filter-list.component';
import { PxWebApiAdapterService } from '../services/px-web-api-adapter.service';
import { LayerService } from '../services/layer.service';
import { LayerListComponent } from '../main-nav/layer-list/layer-list.component';
import { SearchBoxComponent } from '../shared/search-box/search-box.component';
import { FeatureListComponent } from './feature-list/feature-list.component';
import { SearchComponent } from './search/search.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { map, tap } from 'rxjs/operators';
import { LayoutService } from '../services/layout.service';
import { A11yModule } from "@angular/cdk/a11y";
import { DataSeriesComponent } from './data-series/data-series.component';
import { MainActionViewService } from '../services/main-action-view.service';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';

/*
  Represents the view state the component is currently in
*/
enum ViewState {
  search,           //Component is showing a search actionViewItem
  featureSelection, //Component is showing a selection of feature to click on
  filter,           //Component is showing a PXWeb filter
  mapsLayerList,    //Component is showing a maps layer list
  basemapsLayerList,//Component is showing basemaps layer list
  layerSearch,      //Component is showing a layer search
  information,      //Component is showing an HTML information panel from footer config
  chart             //Component is showing charts(and tables)?
}

interface MainActionViewConfig {
  showSearch?: boolean,
  showLayers?: boolean
}

@Component({
  selector: 'app-main-action-view',
  standalone: true,
  imports: [TableFilterListComponent, AsyncPipe, MatIcon, MatButtonModule,
    LayerListComponent, SearchBoxComponent, FeatureListComponent, SearchComponent,
    MatProgressSpinnerModule, DataSeriesComponent, A11yModule],
  templateUrl: './main-action-view.component.html',
  styleUrl: './main-action-view.component.css'
})

export class MainActionViewComponent implements OnInit, OnDestroy {
  /*
    Allows to tie side effects like RxJS subscriptions to the component’s lifecycle,
    so the subscriptions are automatically cleaned up when the component is destroyed.
    We pass this to things like takeUntilDestroyed() to make sure that the subscription is cleaned up when the component is destroyed.
  */
  private readonly destroyRef = inject(DestroyRef);

  @Input() selText: AppText = {};
  applicationSettings = input<any>();
  mainActionViewConfig: Signal<MainActionViewConfig | undefined> = computed(() => {
    const settings: MainActionViewConfig | undefined = this.applicationSettings()?.option?.mainActionView
    //Default settings the first object, will be overwritten if config has any. if settings is undefined it is not spreaded
    return {...{showLayers: true, showSearch: true}, ...settings}
  })
  @Input() lang: Languages = Languages.Icelandic;

  searchText: WritableSignal<string> = signal("")

  readonly ViewState = ViewState;
  readonly ActionViewItemType = ActionViewItemType;

  //actionViewItems: actionViewItem[] = [];
  currentlySelectedActionViewItem: actionViewItem | null = null;

  //Keeps track of current viewState the component is in
  currentViewState: ViewState | null = null;//ViewState.mapsLayerList;

  //allPreviousFeatureArrived: boolean = true;

  showFeatureImmediately: WritableSignal<boolean> = signal(false)
  isActionViewLoading: WritableSignal<boolean> = signal(false)

  //Mapping of actionViewItem type to corresponding viewState
  private actionViewTypeToViewStateMap: any = {
    featureQueryResult: ViewState.featureSelection,
    search: ViewState.search,
    filter: ViewState.filter,
    mapsLayerList: ViewState.mapsLayerList,
    basemapsLayerList: ViewState.basemapsLayerList,
    layerSearch: ViewState.layerSearch,
    information: ViewState.information,
    chart: ViewState.chart
  }

  private notClearedOnNewClick = [ActionViewItemType.mapsLayerList, 
                                  ActionViewItemType.basemapsLayerList, 
                                  ActionViewItemType.search,
                                  ActionViewItemType.filter]

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    // Close editable layers on ESC if some tickets are open
    if (this.applicationSettings()?.option?.edit && this.mainActionViewService.actionViewItems().length > 1) this.displayService.emitEditFeatureClosed();
  }

  get sanitizedHtml(): SafeHtml {
    const key = this.currentlySelectedActionViewItem?.html;
    const html = key ? (this.selText[key] ?? '') : '';
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  constructor(
    private searchQueryService: SearchQueryService,
    public displayService: DisplayService,
    public mainActionViewService: MainActionViewService,
    private featureService: FeatureService,
    private editFeatureService: EditFeatureService,
    private PXWebService: PxWebApiAdapterService,
    public layerService: LayerService,
    private layoutService: LayoutService,
    private changeDetector: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.subscribeToMainActionViewItem();
    this.subscribeToFeatureResponses();
    this.subscribeToLayerStatusChanges();
    //this.subscribeToDragAndDropSelection();
    this.subscribeToEditFeatureClosed();
    this.subscribeToMapInteractions();
    this.subscribeToDragAndDropFileDropped();

    //if (this.mainActionViewConfig()?.showLayers) {
      //const layerList: actionViewItem = { type: ActionViewItemType.mapsLayerList, text: this.selText.maps, closeable: false, hidden: true}
      //this.mainActionViewService.sendNewMainActionViewItem(layerList)
      //this.setNewActionViewItem(layerList)
    //}

    //if (this.mainActionViewService.actionViewItems().length === 0 && !this.mainActionViewConfig()?.showSearch) {
      //this.displayService.setMainActionViewActive(false);
    //}
    
    this.displayService.setShowStationDataActive(false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.selText) {
      this.updateTextOnActionViewItems()
    }
  }

  ngOnDestroy(): void {
    if (this.editFeatureService.selectInteraction) {
      this.editFeatureService.selectInteraction?.setActive(true);
      this.displayService.clearAllFeatures();
    }
  }
  /**
   * Need to manually update text on the actionView items when language changes since
   * the text is already set in stone when an actionViewItem ticket is created (its text gets decoupled from this.selText
   * on creation of the ticket)
   */
  private updateTextOnActionViewItems(): void {
    this.mainActionViewService.actionViewItems().forEach((actionViewItem: actionViewItem) => {
      switch (actionViewItem.type) {
        case ActionViewItemType.mapsLayerList:
          actionViewItem.text = this.selText.maps
          break;
        case ActionViewItemType.basemapsLayerList:
          actionViewItem.text = this.selText.baseMaps
          break;
        case ActionViewItemType.search:
          actionViewItem.text = this.selText.search + " - " + this.selText.gnName
          break;
        case ActionViewItemType.featureQueryResult:
          actionViewItem.text = actionViewItem.originalData?.metadataLayer.title_lang[this.lang] ?? actionViewItem.text
          break;
        case ActionViewItemType.filter:
          actionViewItem.text = actionViewItem.originalData?.metadataLayer.title_lang[this.lang] ?? actionViewItem.text
          break;
        case ActionViewItemType.layerSearch:
          actionViewItem.text = `${this.selText?.search} - ${actionViewItem.originalData?.metadataLayer?.title_lang?.[this.lang]}`
          break;
        case ActionViewItemType.information:
          // title is fixed from footer config, no dynamic language update needed
          break;
      }
    })
  }

  /**
   * Sets a new actionViewItem to property currentlySelectedActionViewItem and updates the currentViewState to the correct view
   * @param actionViewItem type actionViewItem
   */
  private setNewActionViewItem(actionViewItem: actionViewItem) {
    this.displayService.clearAllFeaturesByLayer(this.currentlySelectedActionViewItem?.originalData?.metadataLayer?.id ?? 0)
    this.currentlySelectedActionViewItem = actionViewItem
    this.currentViewState = this.actionViewTypeToViewStateMap?.[actionViewItem.type]

    if (this.currentViewState === null || this.currentViewState === undefined) {
      console.warn("Could not get a valid viewState. Type of the actionViewItem was: " + actionViewItem.type)
    }
  }

  /**
   * Selects a new action view item. This is a "page", for example place-name search is one page and query results for 1 layer is another...
   * @param actionViewItem actionViewItem
   */
  selectActionViewItem(actionViewItem: actionViewItem | null) {
    if (!actionViewItem || actionViewItem.originalData?.metadataLayer?.type === LayerType.VEKTOR) return;
    this.setNewActionViewItem(actionViewItem);

    if (this.applicationSettings()?.option?.edit) {
      const isDragAndDrop = actionViewItem.originalData?.metadataLayer?.type === LayerType.DragAndDrop;
      if (!isDragAndDrop) this.editFeatureService.addSelectionFromActionViewItem(actionViewItem);
    }
  }
  
  /**
   * Returns which actionViewItem should be selected next when the currently selected one is closed.
   * The desired behaviour is to select the ticket to the left of the closed one, except for the case when
   * the closed one is the first visible ticket(LayerSelector ticket is always there, hidden), 
   * then select the one to the right. But if there is only 1 
   * visible ticket then select the ticket to the left which would be "Gögn"-layerSelector
   * @param indexOfCurrentlySelectedActionViewItem number
   */
  private determineNextActionViewItem(actionViewItemToClose: actionViewItem): actionViewItem {
    const indexOfCurrentlySelectedActionViewItem: number = this.mainActionViewService.actionViewItems().findIndex((avi: actionViewItem) => avi == actionViewItemToClose)
    //Case when there are only 2 tickets, then select the layerSelector ticket
    if (this.mainActionViewService.actionViewItems().length <= 2) {
      return this.mainActionViewService.actionViewItems()[indexOfCurrentlySelectedActionViewItem - 1]
    }
    //Case when the currently selected ticket is the first visible one, then select the one to the right
    if (indexOfCurrentlySelectedActionViewItem === 1) {
      return this.mainActionViewService.actionViewItems()[indexOfCurrentlySelectedActionViewItem + 1]
    }
    //default case, select the one to the left
    return this.mainActionViewService.actionViewItems()[indexOfCurrentlySelectedActionViewItem - 1]
  }

  /**
   * Removes this actionViewItem from the view.
   * @param actionViewItem actionViewItem
   */
  closeActionViewItem(actionViewItem: actionViewItem | null, resetCoordinates?: boolean): void {
    if (!actionViewItem || actionViewItem.type === ActionViewItemType.mapsLayerList) return; // Should never try to close the layer list
    if (this.applicationSettings()?.option?.edit) {
      this.editFeatureService.cancelNewFeature(); // To clear any new feature being drawn
      // If user has made changes to the feature's geometry, we want to reset the coordinates when closing the actionViewItem by button click or Escape
      this.editFeatureService.clearInteractions(false, resetCoordinates);
      this.editFeatureService.selectInteraction?.setActive(true); // To allow future clicks to select features
    }
    if (actionViewItem?.originalData?.metadataLayer.type === LayerType.WEBGL
      && resetCoordinates === true
      && this.editFeatureService.hasActiveSelectedFeatures()) {
      this.editFeatureService.clearSelectedWebGLFeatures();
    }

    //Cleanup for closing search
    if (actionViewItem.type === ActionViewItemType.search) {
      this.searchQueryService.clearPreviousSearchResults()
      this.searchText.set("")
      this.displayService.clearFeaturesByCameFrom("search")
    }
    //Cleanup for closing table filters
    if (actionViewItem.type === ActionViewItemType.filter) {
      this.PXWebService.resetLayer(actionViewItem.originalData?.metadataLayer ?? null)
      //Why the timeout?
      setTimeout(() => this.PXWebService.clearFiltersCache(), 500)
    }
    //Cleanup for closing chart ticket
    if (actionViewItem.type === ActionViewItemType.chart) {
      this.setFeatureSelected(actionViewItem, false)
    }

    const nextSelectedActionViewItem: actionViewItem = this.determineNextActionViewItem(actionViewItem)
    //this.actionViewItems = this.actionViewItems.filter((avi: actionViewItem) => avi != actionViewItem)
    this.mainActionViewService.removeMainActionViewItem(actionViewItem)

    //Close the view if there is nothing more in it
    if (this.mainActionViewService.actionViewItems().length === 0) {
      this.closeComponent()
      if (this.applicationSettings()?.option?.edit) {
        this.editFeatureService.clearInteractions(true, true);
      }
      return
    }

    if (this.applicationSettings()?.option?.edit) {
      // Remove all selectInteraction features and vertices from the selected layer being closed
      this.editFeatureService.hideVertices();
      this.editFeatureService.selectInteraction?.getFeatures().clear();
    }

    //If true then closing some other actionViewItem that is not currently selected. In that case, don't select a new actionViewItem
    if (actionViewItem !== this.currentlySelectedActionViewItem) return;
    //Make sure to clear drawings, the current drawings if any can only be from the actionVireItem that is being closed if we are here
    this.displayService.clearAllFeaturesByLayer(this.currentlySelectedActionViewItem?.originalData?.metadataLayer?.id ?? 0)

    this.setNewActionViewItem(nextSelectedActionViewItem)

    if (this.applicationSettings()?.option?.edit) {
      // Add all featurs to selectInteraction from the new actionView and hide vertices
      this.editFeatureService.addSelectionFromActionViewItem(nextSelectedActionViewItem);
      this.editFeatureService.hideVertices();
    }
  }

  /**
   * Close the whole main-action-view.component. Do some cleanup.
   */
  closeComponent(): void {
    this.editFeatureService.clearInteractions(true, true);
    this.editFeatureService.hideVertices();
    this.displayService.clearFeaturesByCameFrom("click")
    this.displayService.clearFeaturesByCameFrom("search")
    this.searchQueryService.clearPreviousSearchResults()
    this.displayService.setMainActionViewActive(false)
    const filterActionViewItems: actionViewItem[] = this.mainActionViewService.actionViewItems().filter((avi: actionViewItem) => avi.type === ActionViewItemType.filter)
    filterActionViewItems.forEach((avi: actionViewItem) => {
      this.PXWebService.resetLayer(avi.originalData?.metadataLayer ?? null)
    })
    setTimeout(() => this.PXWebService.clearFiltersCache(), 500) //Temp fix: this executes before updateCache() in ngOnDestroy in table-filter.component
  }

  searchChange(event: searchQueryRequest) {
    this.searchText.set(event.searchValue)
    //Close search ticket if searchBox is empty and set selected back to Gögn
    if (event.searchValue.length < 1) {
      this.mainActionViewService.actionViewItems().filter((avi: actionViewItem) => avi.type === ActionViewItemType.search)
      .forEach((avi) => this.mainActionViewService.removeMainActionViewItem(avi))

      const mapsTicket: actionViewItem | undefined = this.mainActionViewService.actionViewItems().find((avi: actionViewItem) => avi.type === ActionViewItemType.mapsLayerList)
      if (mapsTicket) this.setNewActionViewItem(mapsTicket)
      return
    }
    //Add search ticket if its not already there
    const isThereExistingSearchTicket: boolean = this.mainActionViewService.actionViewItems().some((avi: actionViewItem) => avi?.type === ActionViewItemType.search)
    if (!isThereExistingSearchTicket) this.mainActionViewService.sendNewMainActionViewItem({ type: ActionViewItemType.search, text: "Leitarniðurstöður", closeable: true })

    const searchTicket: actionViewItem | undefined = this.mainActionViewService.actionViewItems().find((avi: actionViewItem) => avi.type === ActionViewItemType.search)
    if (!searchTicket) return
    //Set as the selected ticket
    this.setNewActionViewItem(searchTicket)

  }

  selectLayerSelectorActionViewItem(): void {
    const layersTicket: actionViewItem | undefined = this.mainActionViewService.actionViewItems().find((avi: actionViewItem) => avi.type === ActionViewItemType.mapsLayerList)
    if (!layersTicket) return
    this.selectActionViewItem(layersTicket)
  }

  private subscribeToMainActionViewItem(): void {
    this.mainActionViewService.mainActionViewItem$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((newActionViewItem: actionViewItem | null) => {
          if (!newActionViewItem) return
        })
      )
      .subscribe((newActionViewItem: actionViewItem | null) => {
        if (!newActionViewItem) return;
        //console.log("New actionViewItem: ")
        //console.log(newActionViewItem)
        switch (newActionViewItem.type) {
          case ActionViewItemType.search:
            this.setNewActionViewItem(newActionViewItem)
            this.changeDetector.markForCheck();
            break;

          case ActionViewItemType.mapsLayerList:
             this.setNewActionViewItem(newActionViewItem)
             break;

          case ActionViewItemType.featureQueryResult:
            if (!newActionViewItem.originalData) {
              console.warn("Received actionViewItem with type 'featureQueryResult', but no data payload. Cannot process and display feature.");
              return;
            }
            const processedFeatures: wholeLayerFeatureData | null = this.featureService.processFeatures(newActionViewItem.originalData);
            if (!processedFeatures) {
              console.warn("No features found in response for layer: " + newActionViewItem.originalData.metadataLayer.name)
              return
            }
            newActionViewItem.formattedData = processedFeatures

            this.setNewActionViewItem(newActionViewItem)
            //this.displayService.setMainActionViewActive(true) // Ensure the view stays open when no other tickets remained (e.g. config with showLayers and showSearch both false)
            break;

          case ActionViewItemType.filter:
            this.mainActionViewService.actionViewItems().filter(
              (avi: actionViewItem) => avi?.originalData?.metadataLayer?.id === newActionViewItem?.originalData?.metadataLayer?.id
            ).forEach((avi) => this.mainActionViewService.removeMainActionViewItem(avi))
            this.setNewActionViewItem(newActionViewItem)
            break;

          case ActionViewItemType.layerSearch:
            this.setNewActionViewItem(newActionViewItem)
            break;

          case ActionViewItemType.information:
            const existingInfoTicket: actionViewItem | undefined = this.mainActionViewService.actionViewItems().find(
              (avi: actionViewItem) => avi.type === ActionViewItemType.information && avi.html === newActionViewItem.html
            );
            if (existingInfoTicket) {
              this.setNewActionViewItem(existingInfoTicket);
            } else {
              this.setNewActionViewItem(newActionViewItem);
            }
            this.layoutService.updateMainActionViewWidthByRatio(0.33);
            this.changeDetector.markForCheck();
            break;

          case ActionViewItemType.chart:
            this.setNewActionViewItem(newActionViewItem);
            if (window.innerWidth > 900) {
              this.layoutService.updateMainActionViewWidthByRatio(0.5);
            } else {
              this.layoutService.updateMainActionViewWidthByRatio(1.0);
            }
            this.setFeatureSelected(newActionViewItem, true)
        }
        if (this.layoutService.getCurrentmainActionViewWidth() === 0) {
          // Need to update the mainActionView width to init state if it is currently closed, to be able to show the information panel
          this.layoutService.updateMainActionViewWidthToInitState();
        }
      });
  }
  /**
   * Sets the "isSelected" boolean property on a specific feature from the layer in actionViewItem.
   * This is done for styling purposes, shwoing what is selected. In the FlatStyle for the layer there is
   * a conditional cheking the "isSelected" property.
   * NOTE: This is fro MVP of loftgæðasjá, perhaps should not be here in the future and changed if we want it general.
   * @param actionViewItem actionViewItem
   * @param newValue boolean
   * @returns void
   */
  private setFeatureSelected(actionViewItem: actionViewItem, newValue: boolean): void {
    const ticketFeatID = actionViewItem?.originalData?.geoJSONFeature?.features?.[0]?.id
    const layerSource = actionViewItem.originalData?.metadataLayer.layer?.getSource();
    if (!ticketFeatID || !layerSource) return;
    const feature: Feature<Geometry> | null = (layerSource as VectorSource).getFeatureById(ticketFeatID)
    if (!feature) return;
    feature.setProperties({isSelected: newValue})
  }

  private subscribeToFeatureResponses(): void {
    this.featureService.allFeatureResponsesArrived$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isActionViewLoading.set(false); // Stop the spinner
        const allFeatureQueryResultsTickets: actionViewItem[] = this.mainActionViewService.actionViewItems().filter((avi: actionViewItem) => avi.type === ActionViewItemType.featureQueryResult)

        if (allFeatureQueryResultsTickets.length === 1 && allFeatureQueryResultsTickets?.[0].formattedData?.features?.length === 1) {
          this.showFeatureImmediately.set(true)
        }
        this.changeDetector.detectChanges()
      });
  }

  private subscribeToLayerStatusChanges(): void {
    this.layerService.getNewMaplayersStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mapchange: mapChange) => {
        const newCheckedStatus: boolean = mapchange.newCheckStatus;
        if (newCheckedStatus) return;

        const layersCheckedOffIDs: number[] = mapchange.layers.map((ml: mapLayer) => ml.id);
        const associatedActionViewItemToLayer: actionViewItem[] = this.mainActionViewService.actionViewItems().filter(
          (avi: actionViewItem) => layersCheckedOffIDs.includes(avi?.originalData?.metadataLayer?.id ?? -1)
        );
        
        associatedActionViewItemToLayer.forEach((avi: actionViewItem) => this.closeActionViewItem(avi));
      });
  }
  /**
   * Listen for edit feature close events
   */
  private subscribeToEditFeatureClosed(): void {
    this.displayService.editFeatureClosed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closeActionViewItem(this.currentlySelectedActionViewItem, true);
      });
  }

  private subscribeToMapInteractions(): void {
    const mapClick$ = this.featureService.getMapClicked();
    const dragBoxEnd$ = this.featureService.getMapBoxEnd(this.featureService.getDragBox());

    this.featureService.isQueryLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isLoading => this.isActionViewLoading.set(isLoading));

    const merged$: Observable<{ type: 'click' | 'boxend' | 'save' }> = merge(
      mapClick$.pipe(map(() => ({ type: 'click' as const }))),
      dragBoxEnd$.pipe(map(() => ({ type: 'boxend' as const }))),
      this.editFeatureService.isSavingChanges$.pipe(map(() => ({ type: 'save' as const })))
    );

    merged$
      .pipe(
        takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
          // Don't clear action view items while the user is actively editing/creating a feature
          if ((this.displayService.getEditFeatureActive()?.value || this.displayService.getEditFeatureActiveInActionView()?.value) && event.type !== 'save') return;

          //On every new click, clear out all all actionViewItems that are not of the type below
          this.mainActionViewService.actionViewItems().forEach((currentActionViewItems) => {
            if (!this.notClearedOnNewClick.includes(currentActionViewItems.type)) {
              this.closeActionViewItem(currentActionViewItems)
            }
          })
          if (!this.displayService.getEditFeatureActiveInActionView().getValue()) this.showFeatureImmediately.set(false); // Don't show feature list if editing feature

          const featureItems = this.mainActionViewService.actionViewItems().filter((avi: actionViewItem) => avi.type === ActionViewItemType.featureQueryResult);  // Get all featureQueryResult items
      
          featureItems.forEach((avi: actionViewItem) => {
            if (avi.originalData?.metadataLayer?.type === LayerType.DragAndDrop) {
              if (!this.displayService.getEditFeatureActiveInActionView().value) {
                this.closeActionViewItem(avi);
              }
            }
          });
      });
  }

  private subscribeToDragAndDropFileDropped(): void {
    this.editFeatureService.dragAndDropFileDropped$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Close all feature query result tickets when a new file is dropped
        const featureQueryTickets = this.mainActionViewService.actionViewItems().filter((avi: actionViewItem) => avi.type === ActionViewItemType.featureQueryResult);
        featureQueryTickets.forEach((avi: actionViewItem) => this.closeActionViewItem(avi));
      });
  }
}